/**
 * usePhoneSystem - Telefon-Anrufe fuer die Kommunikation.
 *
 * Datenquelle: Supabase-Tabelle eingehende_anrufe (Edge Functions fonio-inbound
 * + fonio-postcall). Twilio wird nicht mehr verwendet. Das Live-Popup liefert
 * <FonioAnrufButton /> global; dieser Hook setzt daher keinen incomingCall.
 *
 * Exportierte Typen + Rueckgabe-API bleiben unveraendert (PhoneSystem.tsx /
 * KommunikationPage.tsx haengen daran).
 */

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { supabase } from '@/lib/supabase';

export type CallDirection = 'inbound' | 'outbound';
export type CallStatus = 'ringing' | 'in-progress' | 'completed' | 'missed' | 'busy' | 'no-answer' | 'canceled' | 'failed' | 'voicemail';
export type ExtensionStatus = 'available' | 'busy' | 'dnd' | 'offline';

export interface PhoneCall {
  id: string;
  created_at: string;
  direction: CallDirection;
  status: CallStatus;
  from_number: string;
  to_number: string;
  extension?: string;
  contact_id?: string;
  contact_name?: string;
  akte_id?: string;
  started_at: string;
  answered_at?: string;
  ended_at?: string;
  duration_seconds: number;
  recording_url?: string;
  recording_duration_seconds?: number;
  transcription?: string;
  notes?: string;
  ai_summary?: string;
  ai_suggested_actions?: AISuggestedAction[];
  twilio_call_sid?: string;
  user_id?: string;
  user_name?: string;
  transferred_from?: string;
  transferred_to?: string;
  transfer_reason?: string;
  callback_requested: boolean;
  callback_note?: string;
  callback_completed_at?: string;
  quality_rating?: number;
  tags?: string[];
}

export interface AISuggestedAction {
  type: 'termin' | 'aufgabe' | 'email' | 'notiz' | 'rueckruf';
  titel: string;
  beschreibung?: string;
  datum?: string;
  empfaenger?: string;
  prioritaet?: 'niedrig' | 'mittel' | 'hoch';
}

export interface PhoneExtension {
  id: string;
  created_at: string;
  updated_at: string;
  extension: string;
  user_id: string;
  user_name: string;
  is_active: boolean;
  status: ExtensionStatus;
  voicemail_enabled: boolean;
  voicemail_greeting_url?: string;
  ring_timeout_seconds: number;
  forward_to_number?: string;
  forward_on_busy: boolean;
  forward_on_no_answer: boolean;
}

export interface PhoneConfig {
  configured: boolean;
  main_number?: string;
  recording_enabled: boolean;
  transcription_enabled: boolean;
  ai_summary_enabled: boolean;
  ai_suggestions_enabled: boolean;
  ivr_enabled: boolean;
}

export interface ActiveCall {
  id: string;
  direction: CallDirection;
  phoneNumber: string;
  contactName?: string;
  contactId?: string;
  akteId?: string;
  status: 'ringing' | 'in-progress' | 'on-hold';
  startedAt: string;
  answeredAt?: string;
  isMuted: boolean;
  isOnHold: boolean;
  isRecording: boolean;
  isSimulation?: boolean;
}

interface EingehenderAnrufRow {
  id: string;
  eingegangen_am: string;
  from_number: string | null;
  to_number: string | null;
  anrufer_name: string | null;
  anrufer_firma: string | null;
  quelle: string;
  crm_kontakt_id: string | null;
  kunde_id: string | null;
  akte_id: string | null;
  akten_nummer: string | null;
  status: string;
  zusammenfassung: string | null;
  anliegen: string | null;
  transkript: string | null;
  dauer_sekunden: number | null;
}

function mapAnruf(a: EingehenderAnrufRow): PhoneCall {
  return {
    id: a.id,
    created_at: a.eingegangen_am,
    direction: 'inbound',
    status: 'completed',
    from_number: a.from_number ?? '',
    to_number: a.to_number ?? '',
    contact_id: a.crm_kontakt_id ?? undefined,
    contact_name: a.anrufer_name ?? undefined,
    akte_id: a.akte_id ?? undefined,
    started_at: a.eingegangen_am,
    duration_seconds: a.dauer_sekunden ?? 0,
    notes: a.anliegen ?? undefined,
    ai_summary: a.zusammenfassung ?? undefined,
    transcription: a.transkript ?? undefined,
    callback_requested: false,
  };
}

export function usePhoneSystem() {
  const [calls, setCalls] = useState<PhoneCall[]>([]);
  const [extensions] = useState<PhoneExtension[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeCall, setActiveCall] = useState<ActiveCall | null>(null);
  const [incomingCall] = useState<ActiveCall | null>(null);
  const [config] = useState<PhoneConfig>({
    configured: true,
    recording_enabled: false,
    transcription_enabled: false,
    ai_summary_enabled: false,
    ai_suggestions_enabled: false,
    ivr_enabled: false,
  });
  const subscriptionRef = useRef<ReturnType<typeof supabase.channel> | null>(null);

  const loadCalls = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('eingehende_anrufe')
        .select('*')
        .order('eingegangen_am', { ascending: false })
        .limit(200);
      if (error) {
        console.error('[usePhoneSystem] Fehler beim Laden der Anrufe:', error);
        return;
      }
      setCalls(((data ?? []) as EingehenderAnrufRow[]).map(mapAnruf));
    } catch (err) {
      console.error('[usePhoneSystem] Fehler beim Laden der Anrufe:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCalls();
  }, [loadCalls]);

  useEffect(() => {
    const channel = supabase
      .channel('eingehende-anrufe-komm')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'eingehende_anrufe' }, (payload) => {
        const c = mapAnruf(payload.new as EingehenderAnrufRow);
        setCalls((prev) => (prev.some((p) => p.id === c.id) ? prev : [c, ...prev]));
      })
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'eingehende_anrufe' }, (payload) => {
        const c = mapAnruf(payload.new as EingehenderAnrufRow);
        setCalls((prev) => prev.map((p) => (p.id === c.id ? c : p)));
      })
      .subscribe();
    subscriptionRef.current = channel;
    return () => {
      if (subscriptionRef.current) supabase.removeChannel(subscriptionRef.current);
    };
  }, []);

  const missedCalls = useMemo<PhoneCall[]>(() => [], []);
  const callbackRequests = useMemo(
    () => calls.filter((c) => c.callback_requested && !c.callback_completed_at),
    [calls],
  );
  const todayCalls = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return calls.filter((c) => new Date(c.started_at) >= today);
  }, [calls]);
  const unreadCount = 0;

  const makeCall = useCallback(async (_toNumber: string, _contactName?: string) => {}, []);
  const answerCall = useCallback(async () => {}, []);
  const rejectCall = useCallback(async () => {}, []);
  const endCall = useCallback(async () => { setActiveCall(null); }, []);
  const toggleMute = useCallback(() => {}, []);
  const toggleHold = useCallback(() => {}, []);
  const toggleRecording = useCallback(() => {}, []);
  const transferCall = useCallback((_toExtension: string) => { setActiveCall(null); }, []);

  const updateCallNotes = useCallback(async (callId: string, notes: string) => {
    setCalls((prev) => prev.map((c) => (c.id === callId ? { ...c, notes } : c)));
  }, []);
  const markCallbackCompleted = useCallback(async (callId: string) => {
    const now = new Date().toISOString();
    setCalls((prev) => prev.map((c) => (c.id === callId ? { ...c, callback_completed_at: now } : c)));
  }, []);
  const requestCallback = useCallback(async (callId: string, note: string) => {
    setCalls((prev) => prev.map((c) => (c.id === callId ? { ...c, callback_requested: true, callback_note: note } : c)));
  }, []);

  return {
    calls, extensions, loading, activeCall, incomingCall, config,
    missedCalls, callbackRequests, todayCalls, unreadCount,
    makeCall, answerCall, rejectCall, endCall, toggleMute, toggleHold, toggleRecording, transferCall,
    updateCallNotes, markCallbackCompleted, requestCallback, loadCalls,
  };
}
